import React from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import type { BusinessTypeChangeBlockers } from '../../../onboarding/application/businessTypeChangeUtils';
import { businessTypeChangeBlockersClear } from '../../../onboarding/application/businessTypeChangeUtils';

type OptionRowProps = {
  title: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  testID?: string;
  danger?: boolean;
};

function OptionRow({ title, description, checked, onToggle, disabled, testID, danger }: OptionRowProps) {
  return (
    <TouchableOpacity
      style={[
        styles.optionCard,
        checked && (danger ? styles.optionCardDangerOn : styles.optionCardSelected),
        danger && styles.optionCardDanger,
      ]}
      onPress={onToggle}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled: Boolean(disabled) }}
      testID={testID}
    >
      <View style={styles.optionHeader}>
        <Ionicons
          name={checked ? 'checkbox' : 'square-outline'}
          size={20}
          color={checked ? (danger ? ticketColors.bad : ticketColors.gold) : ticketColors.ink2}
        />
        <Text style={[styles.optionTitle, checked && !danger && styles.optionTitleSelected]}>
          {title}
        </Text>
      </View>
      <Text style={styles.optionBody}>{description}</Text>
    </TouchableOpacity>
  );
}

type Props = {
  fromLabel: string;
  toLabel: string;
  blockers: BusinessTypeChangeBlockers | null;
  blockersLoading?: boolean;
  onGoCash: () => void;
  onGoOrders: () => void;
  onGoAppointments: () => void;
  understood: boolean;
  onUnderstoodChange: (value: boolean) => void;
  disabled?: boolean;
};

export function BusinessTypeChangeImpactPanel({
  fromLabel,
  toLabel,
  blockers,
  blockersLoading = false,
  onGoCash,
  onGoOrders,
  onGoAppointments,
  understood,
  onUnderstoodChange,
  disabled = false,
}: Props) {
  const clear = blockers ? businessTypeChangeBlockersClear(blockers) : false;
  const rows: Array<{
    ok: boolean;
    text: string;
    goLabel: string;
    onGo: () => void;
  }> = [
    {
      ok: blockers?.openCash === false,
      text: 'Caja cerrada en todas las sucursales',
      goLabel: 'Ir a Caja',
      onGo: onGoCash,
    },
    {
      ok: blockers?.openOrders === false,
      text: 'Sin pedidos online abiertos',
      goLabel: 'Ver pedidos',
      onGo: onGoOrders,
    },
    {
      ok: blockers?.activeAppointments === false,
      text: 'Sin turnos pendientes',
      goLabel: 'Ver agenda',
      onGo: onGoAppointments,
    },
  ];

  return (
    <View style={styles.root}>
      <View style={styles.ticket} testID="change-rubro-ticket">
        <View style={styles.ticketCol}>
          <Text style={styles.ticketName}>{fromLabel}</Text>
          <Text style={styles.ticketSub}>ahora</Text>
        </View>
        <Text style={styles.ticketArrow}>→</Text>
        <View style={styles.ticketCol}>
          <Text style={styles.ticketName}>{toLabel}</Text>
          <Text style={styles.ticketSub}>nuevo</Text>
        </View>
      </View>

      <View style={[styles.card, clear ? styles.cardOk : styles.cardBad]}>
        <Text style={styles.columnTitle}>Antes de pasar</Text>
        {blockersLoading && !blockers ? (
          <ActivityIndicator color={ticketColors.gold} style={styles.blockersLoader} />
        ) : !blockers ? (
          <Text style={styles.blockerHint}>
            No pudimos chequear caja, pedidos y turnos. Cerralos y recargá.
          </Text>
        ) : (
          rows.map((row) => (
            <View key={row.text} style={styles.blockerRow}>
              <View style={[styles.statusDot, row.ok ? styles.statusOk : styles.statusNo]}>
                <Ionicons name={row.ok ? 'checkmark' : 'warning'} size={12} color={ticketColors.paper} />
              </View>
              <Text style={styles.blockerText}>{row.text}</Text>
              {row.ok ? null : (
                <TouchableOpacity onPress={row.onGo} disabled={disabled} accessibilityRole="button">
                  <Text style={styles.goLink}>{row.goLabel}</Text>
                </TouchableOpacity>
              )}
            </View>
          ))
        )}
        {blockers && !clear ? (
          <Text style={styles.blockerHint}>
            Cerrá eso y volvé. Si mandás ahora, el servidor te lo va a rechazar igual.
          </Text>
        ) : null}
      </View>

      <View style={styles.columns}>
        <View style={styles.column}>
          <Text style={styles.columnTitle}>Se borra</Text>
          <Text style={styles.columnItem}>Productos, stock, marcas y proveedores</Text>
          <Text style={styles.columnItem}>Promos ligadas al catálogo</Text>
          <Text style={styles.columnItem}>Mesas y tickets de cocina, si había</Text>
        </View>
        <View style={styles.column}>
          <Text style={styles.columnTitle}>Se queda</Text>
          <Text style={styles.columnItem}>Ventas y cajas cerradas</Text>
          <Text style={styles.columnItem}>Clientes y cuentas</Text>
          <Text style={styles.columnItem}>Empleados, sucursales y el plan</Text>
        </View>
      </View>

      <OptionRow
        title="Entiendo y acepto que se borran productos y stock"
        description="Las ventas y los clientes quedan."
        checked={understood}
        onToggle={() => onUnderstoodChange(!understood)}
        disabled={disabled}
        danger
        testID="change-rubro-ack"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: spacing.md,
  },
  ticket: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  ticketCol: {
    flex: 1,
    minWidth: 0,
  },
  ticketName: {
    ...typography.title3,
    color: ticketColors.ink,
  },
  ticketSub: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: 2,
  },
  ticketArrow: {
    ...typography.title3,
    color: ticketColors.gold,
  },
  card: {
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  cardOk: {
    borderColor: 'rgba(21, 128, 61, 0.35)',
    backgroundColor: 'rgba(21, 128, 61, 0.08)',
  },
  cardBad: {
    borderColor: 'rgba(239, 68, 68, 0.28)',
    backgroundColor: 'rgba(239, 68, 68, 0.06)',
  },
  blockersLoader: {
    marginVertical: spacing.sm,
  },
  blockerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 6,
  },
  statusDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusOk: {
    backgroundColor: ticketColors.ok,
  },
  statusNo: {
    backgroundColor: ticketColors.bad,
  },
  blockerText: {
    ...typography.caption,
    color: ticketColors.ink,
    flex: 1,
  },
  goLink: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  blockerHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: spacing.xs,
  },
  columns: {
    flexDirection: 'row',
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  column: {
    flex: 1,
    minWidth: 220,
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  columnTitle: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  columnItem: {
    ...typography.caption,
    color: ticketColors.ink,
  },
  optionCard: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.md,
    backgroundColor: ticketColors.paper,
    gap: spacing.xs,
  },
  optionCardSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  optionCardDanger: {
    borderColor: 'rgba(239, 68, 68, 0.28)',
    backgroundColor: 'rgba(239, 68, 68, 0.06)',
  },
  optionCardDangerOn: {
    borderColor: ticketColors.bad,
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  optionTitle: {
    ...typography.title3,
    fontSize: 15,
    color: ticketColors.ink,
    flex: 1,
  },
  optionTitleSelected: {
    color: ticketColors.gold,
  },
  optionBody: {
    ...typography.caption,
    color: ticketColors.ink2,
    paddingLeft: 28,
  },
});
